/**
 * App Layout (User-facing)
 *
 * Purpose: Layout for authenticated user pages
 * Features:
 * - Navigation header
 * - User menu
 * - Logout functionality
 */

import { Link, Outlet, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { clearCredentials } from '@/features/auth/slices/authSlice'
import { useLogoutMutation } from '@/features/auth/api/authApi'
import { clearAuthData } from '@/core/auth'
import { logError } from '@/utils/logger'
import { LogOut, User, Home } from 'lucide-react'
import { Button } from '@/features/common/components/Button'

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.3,
}

export function AppLayout() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector(state => state.auth)
  const [logout] = useLogoutMutation()

  const handleLogout = async () => {
    try {
      // Call API logout
      await logout().unwrap()
    } catch (error) {
      logError('Logout failed', error)
    } finally {
      // Clear Redux and local storage
      dispatch(clearCredentials())
      clearAuthData()
      // Redirect to login
      navigate('/auth/login', { replace: true })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-8">
              <Link to="/app" className="flex items-center gap-2">
                <Home className="w-6 h-6 text-primary-600" />
                <span className="text-xl font-bold text-gray-900">Stide</span>
              </Link>
              <nav className="hidden md:flex gap-6">
                <Link to="/app" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Home
                </Link>
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span>{user?.name || 'User'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="initial"
          animate="animate"
          exit="exit"
          variants={pageVariants}
          transition={pageTransition}
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  )
}
